import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGroups } from "features/management/vehicleGroup/groupsThunks";
import AddUserDetailsPopup from "./AddUserDetailsPopup";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EditPopup from "./EditPopup";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  Snackbar,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { fetchUserDetails } from "features/management/userManagement/userThunks";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDPagination from "components/MDPagination";
import FilterCard from "./filterCard";
import { assignGroupToUser, deleteSubUser } from "config/apis";

const TableData = () => {
  const dispatch = useDispatch();
  const { groups, status, error } = useSelector(
    (state) => state.groups
  );
  const searchGroup = useSelector((state) => state.user.searchGroup);
  const details = useSelector((state) => state.user.details);

  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(6);
  const [search, setSearch] = useState("");

  const handleEdit = (userId) => {
    setSelectedGroupId(Number(userId));
    setSelectedUserId(userId);
    setOpenEditPopup(true);
  };

  const handleAssignGroup = async (newGroupId) => {
    const selectedUser = details.find(
      (data) => data.userId === selectedUserId || ""
    );
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem("token");
      const payload = {
        userId: String(selectedUser.userId),
        groupId: String(newGroupId),
        orgId: selectedUser?.orgId || "",
        orgName: selectedUser?.orgName || "",
        ownerOrgId: selectedUser?.ownerOrgId || "",
      };

      const response = await fetch(assignGroupToUser, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to assign group");

      setOpenEditPopup(false);
      dispatch(fetchUserDetails());
    } catch (err) {
      alert("Failed to assign group: " + err.message);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  const handleDeleteClick = (userId) => {
    setGroupToDelete(Number(userId));
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    const userName = details.find((val) => val.userId === groupToDelete);

    try {
      const token = localStorage.getItem("token");
      const emailId = encodeURIComponent(userName.emailId);

      const response = await fetch(`${deleteSubUser}/${emailId}`, {
        method: "delete",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": token,
        },
      });

      if (!response.ok) throw new Error("Failed to delete group");

      const message = await response.text();

      setSnackbar({
        open: true,
        message: message || "User deleted successfully",
        severity: "success",
      });


      setOpenDeleteDialog(false);
      setGroupToDelete(null);
      dispatch(fetchGroups());
      dispatch(fetchUserDetails());
    } catch (err) {
      // alert("Failed to delete group: " + err.message);
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete user",
        severity: "error",
      });

    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setGroupToDelete(null);
  };

  useEffect(() => {
    dispatch(fetchGroups());
    dispatch(fetchUserDetails());
  }, [dispatch]);

  if (status === "loading")
    return (
      <MDBox textAlign="center" mt={5}>
        <CircularProgress />
        <MDTypography variant="body1" mt={2}>
          Loading...
        </MDTypography>
      </MDBox>
    );

  if (status === "failed")
    return (
      <Alert severity="error" sx={{ mt: 2, maxWidth: "500px", mx: "auto" }}>
        {error}
      </Alert>
    );

  const filteredUsers = details.filter((user) =>
    searchGroup || search
      ? (user.groupName || "")
        .toLowerCase()
        .includes((searchGroup || search).toLowerCase())
      : true
  );

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  return (
    <>
      <Paper
        sx={{
          mx: "auto",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          borderBottom="1px solid #eee"
        >
          {/* <MDTypography variant="h5" fontWeight="bold" color="text.primary">
            User Management
          </MDTypography> */}
          <MDBox sx={{ display: "flex", gap: "1rem" }}>
            <TextField
              size="small"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: "250px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
            <FilterCard />
          </MDBox>
        </MDBox>

        <TableContainer sx={{ maxHeight: "90vh" }}>
          <Table stickyHeader>
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                {[
                  "User Name",
                  "Assigned Group",
                  "Assigned Roles",
                  "Action",
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      backgroundColor: "#ffff",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user, index) => (
                  <TableRow
                    key={user.userId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fff" : "white",
                      "&:hover": { backgroundColor: "#fafafa" },
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <MDTypography
                        sx={{ fontWeight: "bold", fontSize: "1rem" }}
                      >
                        {user.userName}
                      </MDTypography>
                      <MDTypography sx={{ fontSize: "0.85rem" }}>
                        {user.emailId}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>{user.groupName}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{user.assignedRole}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <MDBox display="flex" gap={1}>
                        <MDButton
                          onClick={() => handleEdit(user.userId)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            fontSize: "0.8rem",
                            color: "green",
                          }}
                        >
                          <EditIcon />
                        </MDButton>
                        <MDButton
                          onClick={() => handleDeleteClick(user.userId)}
                          sx={{
                            color: "red",
                          }}
                        >
                          <DeleteIcon />
                        </MDButton>
                      </MDBox>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <MDTypography variant="body2" color="text.secondary">
                      No users found
                    </MDTypography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          borderTop="1px solid #eee"
        >
          <MDTypography variant="button" color="secondary">
            Showing {page * rowsPerPage + 1}-
            {Math.min((page + 1) * rowsPerPage, filteredUsers.length)} of{" "}
            {filteredUsers.length} entries
          </MDTypography>

          {totalPages > 1 && (
            <MDPagination color="info" variant="gradient">
              {page > 0 && (
                <MDPagination item onClick={() => setPage(page - 1)}>
                  <KeyboardArrowLeftIcon />
                </MDPagination>
              )}
              {Array.from({ length: totalPages }).map((_, idx) => (
                <MDPagination
                  key={idx}
                  item
                  active={idx === page}
                  onClick={() => setPage(idx)}
                >
                  {idx + 1}
                </MDPagination>
              ))}
              {page < totalPages - 1 && (
                <MDPagination item onClick={() => setPage(page + 1)}>
                  <KeyboardArrowRightIcon />
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      </Paper>
      <EditPopup
        open={openEditPopup}
        onClose={() => setOpenEditPopup(false)}
        groups={groups}
        selectedGroupId={selectedGroupId}
        onSubmit={(groupId) => handleAssignGroup(groupId)}
      />{" "}
      <AddUserDetailsPopup
        open={openPopup}
        onClose={() => setOpenPopup(false)}
      />
      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: { borderRadius: "16px", p: 1, maxWidth: "400px" },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="medium" color="error">
            Confirm Delete
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleCancelDelete}
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            No
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            Yes
          </MDButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

    </>
  );
};

export default TableData;
